import React, { useState } from 'react';
import { Formik, Form, yupToFormErrors } from 'formik';
import styled from 'styled-components';

import DocumentTemplateIcon from 'assets/document-template-icon.svg';
import { P, H2, ButtonPrimary } from '@yuhu-inc/yuhui';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

import * as Yup from 'yup';

import AdditionalServicesUtilities from './form_fields/AdditionalServicesUtilities';
import { DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE } from '../CreateDocumentTemplateDialog';

import { StyledFooterText } from './form_fields/shared_styles/footers';

export const serviceOrUtilityOptions = [
  {
    value: 'service',
    label: 'Service',
  },
  {
    value: 'utility',
    label: 'Utility',
  },
];

const StyledForm = styled(Form)`
  width: ${(props) => (props.fieldForms ? '615px' : '330px')};
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 800px;
  ${media.mobile`
  width: 80%;
  `};
`;

const StyledBodyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledP = styled(P)`
  margin-top: 38px;
  font-size: 18px;
  color: ${colors.SLATE_GREY};
`;

const StyledButtonContainer = styled.div`
  width: 100%;
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(ButtonPrimary)`
  min-width: 140px;
  font-size: 18px !important;
`;

const StyledIcon = styled.img`
  width: 180px;
  height: 180px;
  margin-top: 15px;
  ${media.mobile`
    width: 130px;
    height: 130px;
  `};
`;

Yup.addMethod(Yup.array, 'unique', function (message) {
  return this.test('unique', message, function (list) {
    const mapper = (x) => x.question_name;
    const set = [...new Set(list.map(mapper))];
    const isUnique = list.length === set.length;
    const errors = [];
    const questions = list;
    const duplicateQuestiosIndex = [];

    questions.forEach((l, i) => {
      const name = l.question_name;
      questions.forEach((data, index) => {
        if (data.question_name === name && index !== i) {
          duplicateQuestiosIndex.push(index);
        }
      });
    });

    duplicateQuestiosIndex.forEach((questionIndex) => {
      errors.push(
        this.createError({
          path: `${this.path}[${questionIndex}].question_name`,
          message: 'Question name must be unique',
        })
      );
    });

    // check for the answer name duplication
    this.parent.services_and_utilities.forEach((serviceUtility, questionIndex) => {
      const duplicateAnswersIndex = [];
      const answers = serviceUtility.answers;

      answers.forEach((l, i) => {
        const name = l.answer_name;
        answers.forEach((answer, answerIndex) => {
          if (answer.answer_name === name && answerIndex !== i) {
            duplicateAnswersIndex.push(answerIndex);
          }
        });
      });

      duplicateAnswersIndex.forEach((index) => {
        errors.push(
          this.createError({
            path: `${this.path}[${questionIndex}].answers[${index}].answer_name`,
            message: 'Answer name must be unique',
          })
        );
      });

      // check for the conditional question uniqueness
      answers.forEach((answer, answerIndex) => {
        const duplicateConditionalQuestionsIndex = [];
        const conditional_questions = answer.conditional_questions;

        conditional_questions.forEach((l, i) => {
          const name = l.name;
          conditional_questions.forEach((conditionalQuestion, conditionalQuestionIndex) => {
            if (conditionalQuestion.name === name && conditionalQuestionIndex !== i) {
              duplicateConditionalQuestionsIndex.push(conditionalQuestionIndex);
            }
          });
        });

        duplicateConditionalQuestionsIndex.forEach((duplicateConditionalQuestionIndex) => {
          errors.push(
            this.createError({
              path: `${this.path}[${questionIndex}].answers[${answerIndex}].conditional_questions[${duplicateConditionalQuestionIndex}].name`,
              message: 'Conditional question name must be unique',
            })
          );
        });

        // check for the conditional answer uniqueness
        conditional_questions.forEach((conditionalQuestion, conditionalQuestionIndex) => {
          const duplicateConditionalAnswersIndex = [];
          const conditional_answers = conditionalQuestion.conditional_answers;

          conditional_answers.forEach((l, i) => {
            const name = l.name;
            conditional_answers.forEach((conditionalAnswer, conditionalAnswerIndex) => {
              if (conditionalAnswer.name === name && conditionalAnswerIndex !== i) {
                duplicateConditionalAnswersIndex.push(conditionalAnswerIndex);
              }
            });
          });

          duplicateConditionalAnswersIndex.forEach((duplicateConditionalAnswerIndex) => {
            errors.push(
              this.createError({
                path: `${this.path}[${questionIndex}].answers[${answerIndex}].conditional_questions[${conditionalQuestionIndex}].conditional_answers[${duplicateConditionalAnswerIndex}].name`,
                message: 'Conditional answer name must be unique',
              })
            );
          });
        });
      });
    });

    if (errors.length > 0) {
      throw new Yup.ValidationError(errors);
    }

    return isUnique;
  });
});

const AdditionalGroupsValidationSchema = {
  services_and_utilities: Yup.array()
    .of(
      Yup.object().shape({
        question_name: Yup.string().required('Required'),
        answer_type: Yup.string().required('Required'),
        service_or_utility: Yup.string().required('Required'),
        answers: Yup.array().of(
          Yup.object().shape({
            answer_name: Yup.string().required('Required'),
            conditional_questions: Yup.array().of(
              Yup.object().shape({
                name: Yup.string().required('Required'),
                type: Yup.string().required('Required'),
                conditional_answers: Yup.array().of(
                  Yup.object().shape({
                    name: Yup.string().required('Required'),
                  })
                ),
              })
            ),
          })
        ),
      })
    )
    .unique('No duplicate value allowed'),
};

const validateForm = async (values) => {
  const schema = Yup.object().shape(AdditionalGroupsValidationSchema);

  return schema.validate(values, { abortEarly: false }).catch((error) => {
    throw yupToFormErrors(error);
  });
};

export const DocumentTemplateServiceUtilitiesPage = ({
  forward,
  setTemplate,
  documentTemplate,
  goTo,
  handleCreateTemplate,
  ...rest
}) => {
  const servicesAndUtilities = documentTemplate?.services_and_utilities;
  const [fieldForms, setFieldForms] = useState(servicesAndUtilities?.length);

  const handleAddServiceUtility = (values, setFieldValue) => {
    let newServiceAndUtilties = [...values.services_and_utilities];
    newServiceAndUtilties.push({
      question_name: '',
      answer_type: 'radio',
      service_or_utility: 'service', // value can be 'service' or 'utility'
      answers: [
        {
          answer_name: '',
          answer_value: '',
          selected: true,
          conditional_questions: [],
        },
        {
          answer_name: '',
          answer_value: '',
          selected: false,
          conditional_questions: [],
        },
      ],
    });

    if (newServiceAndUtilties.length > 0) {
      setFieldForms(true);
    } else {
      setFieldForms(false);
    }
    setFieldValue('services_and_utilities', newServiceAndUtilties);
  };

  const removeServicesAndUtilitiesFields = () => {
    setFieldForms(false);
  };

  const handleSubmit = (values) => {
    setTemplate((template) => {
      return {
        ...template,
        ...values,
      };
    });
    if (values.services_and_utilities?.length === 0) {
      // If there is no additional service and utilities, skip review page and go to configure page
      // If this needs to change, check the handle back on CreateDocumentTemplateDialog.js as well
      handleCreateTemplate();
      goTo(DOCUMENT_TEMPLATE_CONFIGURE_SIGNATURES_PAGE);
    } else {
      forward();
    }
  };

  return (
    <Formik
      initialValues={{
        services_and_utilities: servicesAndUtilities || [],
      }}
      validate={validateForm}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <StyledForm fieldForms={fieldForms} {...rest}>
            <VerticalFormWrapper
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <H2 contentKey="landlordPortal.documentTemplate.serviceAndUtilties" centered />
              {fieldForms ? (
                <AdditionalServicesUtilities
                  values={values}
                  setFieldValue={setFieldValue}
                  removeServicesAndUtilitiesFields={removeServicesAndUtilitiesFields}
                  errors={errors}
                />
              ) : (
                <React.Fragment>
                  <StyledBodyContainer>
                    <React.Fragment>
                      <StyledIcon src={DocumentTemplateIcon} />
                      <StyledP
                        contentKey="landlordPortal.documentTemplate.addServiceAndUtilties"
                        centered
                      />
                    </React.Fragment>
                  </StyledBodyContainer>
                  <StyledButtonContainer>
                    <StyledButton
                      type="button"
                      contentKey="landlordPortal.documentTemplate.addQuestion"
                      onClick={() => handleAddServiceUtility(values, setFieldValue)}
                    />
                    <StyledButton contentKey="landlordPortal.inspections.next" />
                  </StyledButtonContainer>
                  <StyledFooterText>
                    {getTranslatedString('landlordPortal.documentTemplate.completedPagesAutoSave')}
                  </StyledFooterText>
                </React.Fragment>
              )}
            </VerticalFormWrapper>
          </StyledForm>
        );
      }}
    </Formik>
  );
};
